import type { Role } from "@amber/contracts";
import { PRIORITY_WEIGHT_RULES, ruleValue, type RuleValues } from "./rules.js";
import type { TASK_TYPES } from "./records.js";
import { DAY_MS } from "@amber/contracts";

type TaskType = (typeof TASK_TYPES)[number];

export interface TaskTypeInfo {
  readonly label: string;
  readonly plural: string;
  readonly lowestRole: Exclude<Role, "viewer">;
  readonly weightRule: (typeof PRIORITY_WEIGHT_RULES)[keyof typeof PRIORITY_WEIGHT_RULES];
  readonly weightLabel: string;
}

export const TASK_TYPE_INFO: Record<TaskType, TaskTypeInfo> = {
  failed_items: {
    label: "Failed items",
    plural: "Failed items",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.failed_items,
    weightLabel: "Failed items",
  },
  source_access: {
    label: "Waiting items",
    plural: "Waiting items",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.failed_items,
    weightLabel: "Waiting items",
  },
  coverage_gap: {
    label: "Gaps",
    plural: "Gaps",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.gap,
    weightLabel: "Gap",
  },
  route: {
    label: "Quarantined routes",
    plural: "Quarantined routes",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.platform_issue,
    weightLabel: "Quarantined route",
  },
  platform_issue: {
    label: "Platform-wide issues",
    plural: "Platform-wide issues",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.platform_issue,
    weightLabel: "Platform-wide cause",
  },
  operational_failure: {
    label: "Operational failures",
    plural: "Operational failures",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.failed_items,
    weightLabel: "Operational failure",
  },
  suggested_sources: {
    label: "Suggested sources",
    plural: "Suggested sources",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.suggested_sources,
    weightLabel: "Suggested sources",
  },
  draft_profile: {
    label: "Draft profiles",
    plural: "Draft profiles",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.suggested_sources,
    weightLabel: "Draft profile",
  },
  duplicate: {
    label: "Possible duplicates",
    plural: "Possible duplicates",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.identity,
    weightLabel: "Duplicate check",
  },
  identity_change: {
    label: "Identity changes",
    plural: "Identity changes",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.identity,
    weightLabel: "Identity check",
  },
  communication_match: {
    label: "Uncertain matches",
    plural: "Uncertain matches",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Uncertain match",
  },
  date_conflict: {
    label: "Date conflicts",
    plural: "Date conflicts",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Date conflict",
  },
  carry_over: {
    label: "Corrections in another copy",
    plural: "Corrections in another copy",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Correction in another copy",
  },
  correction_reconciliation: {
    label: "Corrections to reconcile",
    plural: "Corrections to reconcile",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Correction to reconcile",
  },
  reported_problem: {
    label: "Reported problems",
    plural: "Reported problems",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Reported problem",
  },
  plan_review: {
    label: "Plan reviews",
    plural: "Plan reviews",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.plan_review,
    weightLabel: "Plan review",
  },
  optional_review: {
    label: "Optional reviews",
    plural: "Optional reviews",
    lowestRole: "editor",
    weightRule: PRIORITY_WEIGHT_RULES.reported_problem,
    weightLabel: "Optional review",
  },
  budget: {
    label: "Budget alerts",
    plural: "Budget alerts",
    lowestRole: "admin",
    weightRule: PRIORITY_WEIGHT_RULES.platform_issue,
    weightLabel: "Budget alert",
  },
  drill: {
    label: "Planned drills",
    plural: "Planned drills",
    lowestRole: "operator",
    weightRule: PRIORITY_WEIGHT_RULES.plan_review,
    weightLabel: "Planned drill",
  },
};

export interface PriorityInput {
  readonly type: TaskType;
  readonly requiredRoute: boolean;
  readonly openedAt: Date;
  readonly people: number;
  readonly items: number;
}

export interface PriorityBreakdown {
  type_weight: number;
  type_label: string;
  age: number;
  age_days: number;
  impact_people: number;
  impact_items: number;
  score: number;
  level: "high" | "medium" | "low";
}

function priorityLevel(score: number, rules: RuleValues): PriorityBreakdown["level"] {
  if (score >= ruleValue(rules, "priority.high_from")) return "high";
  return score >= ruleValue(rules, "priority.medium_from") ? "medium" : "low";
}

export function taskPriority(input: PriorityInput, now: Date, rules: RuleValues): PriorityBreakdown {
  const info = TASK_TYPE_INFO[input.type];
  const required = input.type === "coverage_gap" && input.requiredRoute;
  const typeWeight = ruleValue(rules, required ? PRIORITY_WEIGHT_RULES.required_gap : info.weightRule);
  const ageDays = Math.max(0, Math.floor((now.getTime() - input.openedAt.getTime()) / DAY_MS));
  const age = Math.min(ageDays * ruleValue(rules, "priority.age_per_day"), ruleValue(rules, "priority.age_max"));
  const impactPeople = Math.min(
    input.people * ruleValue(rules, "priority.per_person"),
    ruleValue(rules, "priority.people_max"),
  );
  const impactItems = Math.min(
    Math.floor(input.items / ruleValue(rules, "priority.items_per_point")),
    ruleValue(rules, "priority.items_max"),
  );
  const score = typeWeight + age + impactPeople + impactItems;
  const level = priorityLevel(score, rules);
  return {
    type_weight: typeWeight,
    type_label: required ? "Required-route gap" : info.weightLabel,
    age,
    age_days: ageDays,
    impact_people: impactPeople,
    impact_items: impactItems,
    score,
    level,
  };
}
